import { useCountryCode } from "@src/packages/i18n/useRegion";
import { useActiveCycle } from "@src/packages/cycles/hooks/useCycles";
import { useCareTeam } from "@src/packages/care/hooks/useCareTeam";
import {
  resolveEmergencyNumbers,
  type EmergencyContacts,
} from "../emergencyNumbers";

/**
 * Who she should call, resolved before she needs it.
 *
 * Both halves come from production data — her cycle's care team for the clinic,
 * the device's region for the emergency services.
 *
 * Called from the top of `InjectionCoachWalk`, not from the paused card, so the
 * queries start when the coach opens and have the whole walk to land. Asking at
 * the moment of the pause would be asking at the worst moment: she would watch a
 * fallback button turn into a phone number while being told to get emergency
 * help.
 *
 * Every field is nullable and the caller must handle that. A patient with no
 * active cycle, a care team with no phone on file, or an unlisted country all
 * produce `null`, and the escalation card falls back to the care-team screen so
 * it is never a dead end.
 */

export type CoachClinicContact = {
  /** The clinic's name where the cycle records one — used to address her. */
  name: string | null;
  /** As recorded, spaces and all. Dial with `telHref`. */
  phone: string;
};

export type CoachEscalationContacts = {
  emergency: EmergencyContacts | null;
  clinic: CoachClinicContact | null;
};

export function useCoachEscalationContacts(): CoachEscalationContacts {
  const countryCode = useCountryCode();
  const { data: activeCycle } = useActiveCycle();
  const { data: careTeam } = useCareTeam(activeCycle?.id);

  // The first member who left a number. The care team is ordered by the API and
  // a cycle usually has one reachable line; picking the first with a phone beats
  // showing her a list to choose from at the one moment choosing is hardest.
  const phone = careTeam
    ?.map((member) => member.phoneNumber?.trim())
    .find((value): value is string => Boolean(value));

  return {
    emergency: resolveEmergencyNumbers(countryCode),
    clinic: phone ? { name: activeCycle?.provider ?? null, phone } : null,
  };
}
